import type { gvgMapEn } from './en.ts'

/**
 * Russian locale catalog for the tactical GvG map sub-domain.
 *
 * Source of truth: `gvgMapEn` (`./en.ts`). Russian must satisfy
 * `typeof gvgMapEn` exactly so the type system catches every structural
 * drift between the two catalogs.
 *
 * Translation notes:
 *   - "Forest / Tree / Boss" are the in-game tactical terms; the Russian
 *     localization keeps the English tactical vocabulary because the
 *     guild-meta uses these names verbatim in the tag catalog (which is
 *     persisted data — do NOT translate those keys here).
 *   - The numbers in `positionPercent` stay as raw digits because they
 *     are read by screen readers as natural ordinals; we adjust the
 *     surrounding Russian phrasing so the sentence stays grammatical
 *     regardless of gender/case.
 *   - "Officer" is rendered as "Офицер" to match the role vocabulary
 *     used elsewhere in the app (`shared/ui/locales/ru.ts`).
 */
export const gvgMapRu: typeof gvgMapEn = {
  canvas: {
    title: 'Позиции на поле боя',
    helpEditable:
      'Карта показывает игроков с тегами Forest, Tree и Boss. Нажмите на точку Forest, Tree или Boss, чтобы отредактировать игроков с этим тегом. Нажмите на группу, чтобы изменить её состав, или перетащите группу либо Tree, чтобы переместить их.',
    helpViewOnlyLeadIn: 'Карта показывает игроков с тегами Forest, Tree и Boss.',
    helpViewOnly: 'Позиции на карте доступны только для просмотра.',
    officerEditBadge: 'Редактирование офицера',
    regionLabel: 'Поле боя GvG',
    imageAlt: 'Поле боя GvG в Where Winds Meet',
  },
  drawer: {
    eyebrow: 'Карта рейда · назначения по тегам',
    loading: 'Загрузка плана карты…',
    retry: 'Повторить',
    help: 'Маркеры Forest, Tree и Boss показывают игроков с подходящими актуальными тегами. Офицеры могут нажать на маркер, чтобы выбрать игроков с этим тегом. Перетащите кружки групп или Tree, чтобы сохранить их позиции.',
    emptyGroups: 'В этом рейде пока нет групп Атаки или Защиты для размещения на карте.',
    titleFallback: 'План карты GvG',
    titleForRaid: (raidName: string) => `${raidName} · План карты`,
  },
  groupMarker: {
    positionPercent: (xPercent: number, yPercent: number) =>
      `Позиция на карте: ${xPercent}% слева, ${yPercent}% сверху.`,
    editPrompt:
      ' Нажмите, чтобы изменить состав группы. Перетащите или используйте стрелки, чтобы переместить.',
    fallbackTitle: 'Нет назначенных игроков',
    fallbackLabel: (markerNumber: string) => `Группа ${markerNumber}`,
  },
  tagMarker: {
    rosterLabel: (objectiveLabel: string, playerCount: number) =>
      `${objectiveLabel}: ${playerCount} ${playerCount === 1 ? 'игрок' : playerCount >= 2 && playerCount <= 4 ? 'игрока' : 'игроков'}. Сфокусируйтесь, чтобы посмотреть состав.`,
    editPlayersPrompt: ' Нажмите, чтобы изменить игроков с этим тегом.',
    editAndMovePrompt:
      ' Нажмите, чтобы изменить игроков с этим тегом. Перетащите или используйте стрелки, чтобы переместить.',
    movableAction: 'Нажмите, чтобы изменить · перетащите для перемещения',
    staticAction: 'Нажмите, чтобы изменить игроков',
  },
  popover: {
    movableIconTitle: 'Подвижный маркер карты',
    emptyRoster: 'Нет назначенных игроков',
    playerCountSingular: (count: number) => `${count} игрок`,
    playerCountPlural: (count: number) =>
      `${count} ${count >= 2 && count <= 4 ? 'игрока' : 'игроков'}`,
  },
}